import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatRupiah } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Wallet, Loader2, FileText, ShoppingBag, Receipt, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const curMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthLabel = (m) => { const [y, mm] = m.split("-"); return `${MONTHS[Number(mm) - 1]} ${y}`; };
const fmtD = (d) => (d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const PAY = { paid: ["Lunas", "bg-emerald-100 text-emerald-700"], dp: ["DP", "bg-blue-100 text-blue-700"], unpaid: ["Belum Lunas", "bg-yellow-100 text-yellow-800"] };

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 36; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

function Stat({ label, value, icon: Icon, testid, dark }) {
  return (
    <div className={`rounded-2xl p-5 flex items-start justify-between ${dark ? "bg-[#00133F] text-white" : "bg-white border border-slate-200"}`}>
      <div>
        <p className={`text-xs font-medium ${dark ? "text-white/70" : "text-slate-500"}`}>{label}</p>
        <p className={`font-heading text-2xl sm:text-3xl font-bold mt-1 ${dark ? "" : "text-[#00133F]"}`} data-testid={testid}>{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dark ? "bg-[#F0005A]" : "bg-[#F0005A]/10"}`}><Icon className={`w-5 h-5 ${dark ? "text-white" : "text-[#F0005A]"}`} /></div>
    </div>
  );
}

function Empty({ text, testid }) {
  return <div className="p-16 text-center text-slate-400" data-testid={testid}><FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />{text}</div>;
}

function WebTable({ web }) {
  if (!web.orders.length) return <Empty text="Belum ada pesanan web lunas pada periode ini." testid="report-web-empty" />;
  return (
    <table className="w-full text-sm min-w-[640px]" data-testid="report-web-table">
      <thead className="bg-slate-50 text-xs font-medium text-slate-500"><tr>
        <th className="p-3 text-left">No. Pesanan</th><th className="p-3 text-left">Pelanggan</th><th className="p-3 text-left">Tanggal Bayar</th>
        <th className="p-3 text-left">Kota</th><th className="p-3 text-right">Ongkir</th><th className="p-3 text-right">Total</th>
      </tr></thead>
      <tbody>{web.orders.map((o) => (
        <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`report-row-${o.order_number}`}>
          <td className="p-3 font-mono font-semibold text-[#00133F]">#{o.order_number}</td>
          <td className="p-3 text-[#00133F]">{o.shipping_address?.recipient_name || o.user_email}</td>
          <td className="p-3 text-slate-500 whitespace-nowrap">{fmtD(o.paid_at || o.created_at)}</td>
          <td className="p-3 text-slate-500">{o.shipping_address?.city}</td>
          <td className="p-3 text-right text-slate-500 whitespace-nowrap">{formatRupiah(o.shipping?.cost || 0)}</td>
          <td className="p-3 text-right font-semibold text-[#F0005A] whitespace-nowrap">{formatRupiah(o.total)}</td>
        </tr>))}
      </tbody>
      <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
        <td className="p-3 text-[#00133F]" colSpan={4}>Omzet Web · Bersih {formatRupiah(web.net)}</td>
        <td className="p-3 text-right text-slate-600">{formatRupiah(web.shipping)}</td>
        <td className="p-3 text-right text-[#F0005A]" data-testid="report-web-total">{formatRupiah(web.omzet)}</td>
      </tr></tfoot>
    </table>
  );
}

function InvoiceTable({ inv }) {
  if (!inv.invoices.length) return <Empty text="Belum ada invoice manual pada periode ini." testid="report-invoice-empty" />;
  return (
    <table className="w-full text-sm min-w-[720px]" data-testid="report-invoice-table">
      <thead className="bg-slate-50 text-xs font-medium text-slate-500"><tr>
        <th className="p-3 text-left">No. Invoice</th><th className="p-3 text-left">Klien</th><th className="p-3 text-left">Tanggal</th>
        <th className="p-3 text-right">Tagihan</th><th className="p-3 text-right">Dibayar</th><th className="p-3 text-left">Status</th>
      </tr></thead>
      <tbody>{inv.invoices.map((x) => {
        const [lbl, cls] = PAY[x.payment_status] || PAY.unpaid;
        return (
          <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`report-inv-row-${x.invoice_number}`}>
            <td className="p-3 font-mono font-semibold text-[#00133F] whitespace-nowrap">{x.invoice_number}</td>
            <td className="p-3 text-[#00133F]">{x.buyer_name}</td>
            <td className="p-3 text-slate-500 whitespace-nowrap">{fmtD(x.date || x.created_at)}</td>
            <td className="p-3 text-right whitespace-nowrap">{formatRupiah(x.grand_total)}</td>
            <td className="p-3 text-right font-semibold text-[#F0005A] whitespace-nowrap">{formatRupiah(Math.min(x.paid_amount || 0, x.grand_total))}</td>
            <td className="p-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{lbl}</span></td>
          </tr>
        );
      })}</tbody>
      <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
        <td className="p-3 text-[#00133F]" colSpan={3}>Sisa piutang {formatRupiah(inv.outstanding)}</td>
        <td className="p-3 text-right text-slate-600">{formatRupiah(inv.billed)}</td>
        <td className="p-3 text-right text-[#F0005A]" data-testid="report-invoice-total">{formatRupiah(inv.omzet)}</td><td />
      </tr></tfoot>
    </table>
  );
}

export default function AdminReport() {
  const [month, setMonth] = useState(curMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const options = useMemo(monthOptions, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/revenue-report?month=${month}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat laporan"))
      .finally(() => setLoading(false));
  }, [month]);

  const s = data?.summary;
  const v = (n) => (loading ? "…" : formatRupiah(n || 0));

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#F0005A] mb-4" data-testid="report-back-link"><ArrowLeft className="w-4 h-4" /> Kembali ke Dashboard</Link>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#00133F]">Laporan Pendapatan</h1>
            <p className="text-sm text-slate-500 mt-1">Gabungan toko online & invoice manual · {monthLabel(month)}</p>
          </div>
          <div className="w-56">
            <label className="text-xs font-medium text-slate-500">Periode (Bulan & Tahun)</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="rounded-xl mt-1 bg-white" data-testid="report-month-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{options.map((m) => <SelectItem key={m} value={m} data-testid={`month-${m}`}>{monthLabel(m)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="report-summary">
          <Stat dark label={`Total Omzet · ${monthLabel(month)}`} value={v(s?.total_omzet)} icon={Wallet} testid="report-total-omzet" />
          <Stat label="Pendapatan Bersih (omzet − ongkir)" value={v(s?.net_revenue)} icon={TrendingUp} testid="report-net-revenue" />
          <Stat label="Total Pesanan / Invoice" value={loading ? "…" : `${s?.order_count || 0} / ${s?.invoice_count || 0}`} icon={Receipt} testid="report-count" />
        </div>

        <Tabs defaultValue="web">
          <TabsList className="mb-4 bg-white border border-slate-200 rounded-full p-1 h-auto">
            <TabsTrigger value="web" className="rounded-full px-5 py-2 data-[state=active]:bg-[#00133F] data-[state=active]:text-white" data-testid="report-tab-web"><ShoppingBag className="w-4 h-4 mr-1.5" /> Laporan Web</TabsTrigger>
            <TabsTrigger value="invoice" className="rounded-full px-5 py-2 data-[state=active]:bg-[#00133F] data-[state=active]:text-white" data-testid="report-tab-invoice"><FileText className="w-4 h-4 mr-1.5" /> Laporan Invoice</TabsTrigger>
          </TabsList>
          {["web", "invoice"].map((t) => (
            <TabsContent key={t} value={t}>
              <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                {loading || !data ? <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
                  : t === "web" ? <WebTable web={data.web} /> : <InvoiceTable inv={data.invoice} />}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
